import { AxiosRequestConfig } from "axios";
import { UseAxiosResult } from "axios-hooks";
import formatUrl from "utils/formatUrl";

import api from "config/urls/api";

import { PagedResponse, UseManageUserManagementApi } from "./setup";

interface PageProps {
  page: number;
  pageSize?: number;
  query?: string;
  orderBy?: string;
}

export interface ClientLoginSetting {
  codatClientId: string;
  name: string;
  typeId: number;
  enabled: boolean;
  isThirdParty: boolean;
}

export interface CodatError {
  correlationId: string;
  error: string;
  service: string;
  statusCode: number;
}

export interface CodatClientInvitationCreate {
  codatClientId: string;
  email: string;
  role: string;
  inviteExpiresOn?: string;
}

export type UserRole = "Administrator" | "Developer" | "Analyst" | "Onboarding";

export interface CodatUser {
  name: string;
  id: string;
  email: string;
  role: UserRole;
  enabled: boolean;
  confirmed: boolean;
  inviteExpiresOn?: string;
  infoMessage: string;
}

export type ApiError = {
  statusCode: number;
  service: string;
  correlationId: string;
  error: string;
};

export const useClientLoginQuery = (
  options: AxiosRequestConfig = {}
): UseAxiosResult<ClientLoginSetting[]> => {
  return UseManageUserManagementApi<ClientLoginSetting[]>(
    {
      url: formatUrl(api.manage.clientLogin.settings),
      ...options,
    },
    {
      manual: true,
    }
  );
};

export const useClientLoginSettingMutation = (): UseAxiosResult => {
  return UseManageUserManagementApi(
    {
      url: formatUrl(api.manage.clientLogin.settings),
      method: "PUT",
    },
    {
      manual: true,
    }
  );
};

export const useUsersQuery = (
  queryParams: PageProps
): UseAxiosResult<PagedResponse<CodatUser>> => {
  return UseManageUserManagementApi<PagedResponse<CodatUser>>({
    url: formatUrl(api.manage.users.all),
    params: queryParams,
  });
};

export const useAddUserMutation = (): UseAxiosResult<CodatUser> => {
  return UseManageUserManagementApi<CodatUser>(
    {
      url: formatUrl(api.manage.users.all),
      method: "POST",
    },
    {
      manual: true,
    }
  );
};

export const useEditUserMutation = (): UseAxiosResult<CodatUser> => {
  return UseManageUserManagementApi<CodatUser>(
    {
      url: formatUrl(api.manage.users.all),
      method: "PUT",
    },
    {
      manual: true,
    }
  );
};

interface DeleteUserProps {
  userId: string;
}

export const useDeleteUserMutation = ({
  userId,
}: DeleteUserProps): UseAxiosResult<CodatUser> => {
  return UseManageUserManagementApi<CodatUser>(
    {
      url: formatUrl(api.manage.users.one, { userId }),
      method: "DELETE",
    },
    {
      manual: true,
    }
  );
};

export const useUserRolesQuery = (): UseAxiosResult<UserRole[]> => {
  return UseManageUserManagementApi<UserRole[]>({
    url: formatUrl(api.manage.users.roles),
  });
};

export interface ChangePasswordBody {
  currentPassword: string;
  newPassword: string;
}
export const useChangePassword = (userId: string): UseAxiosResult => {
  return UseManageUserManagementApi(
    {
      url: formatUrl(api.manage.users.changePassword, { userId }),
      method: "PUT",
    },
    { manual: true }
  );
};
